﻿import { useEffect, useState, useMemo } from 'react';
import {
  Page, Card, Button, Banner, Text, Select,
  DataTable, EmptyState, Spinner, Layout, BlockStack,
  InlineStack, Box, Pagination, Tooltip, Link, InlineGrid,
} from '@shopify/polaris';
import { CartAbandonedIcon, CashDollarIcon, EmailIcon } from '@shopify/polaris-icons';
import { useAbandonedCart } from '../../hooks/useAbandonedCart';
import type { AbandonedCartRecovery } from '../../api/abandonedCart';
import { StatCard } from '../shared/StatCard';
import { IconBadge } from '../shared/IconBadge';

interface DetailProps {
  recovery: AbandonedCartRecovery;
  onBack: () => void;
  onDelete: (id: number) => void;
}

function RecoveryDetail({ recovery, onBack, onDelete }: DetailProps) {
  return (
    <Page fullWidth
      title={`Abandoned Cart Recovery (${recovery.min_hours_since_abandoned}+ hours since abandoned)`}
      backAction={{ content: 'All Recoveries', onAction: onBack }}
      secondaryActions={[{
        content: 'Delete',
        destructive: true,
        onAction: () => { onDelete(recovery.id); onBack(); },
      }]}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="100">
                <Text as="p" variant="bodySm" tone="subdued">Checkouts analysed</Text>
                <Text as="p" variant="heading2xl" fontWeight="bold">{recovery.checkouts_analyzed}</Text>
              </BlockStack>
            </Card>

            {recovery.ai_summary && (
              <Card>
                <BlockStack gap="200">
                  <Text as="h2" variant="headingMd">Summary</Text>
                  <Text as="p" variant="bodyMd">{recovery.ai_summary}</Text>
                </BlockStack>
              </Card>
            )}

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Recovery Emails</Text>
                {recovery.ai_recommendations.length === 0 ? (
                  <Text as="p" tone="subdued">No recommendations generated.</Text>
                ) : (
                  <BlockStack gap="300">
                    {recovery.ai_recommendations.map((r, i) => {
                      const candidate = recovery.candidates.find(
                        (c) => c.customer_name === r.customer_name && c.product_titles === r.product_titles
                      );
                      return (
                        <Card key={i} background="bg-surface-secondary">
                          <BlockStack gap="150">
                            <InlineStack gap="200" blockAlign="center" align="space-between">
                              <InlineStack gap="300" blockAlign="center" wrap={false}>
                                <IconBadge icon={EmailIcon} color="orange" />
                                <Text as="h3" variant="headingSm">{r.customer_name}</Text>
                              </InlineStack>
                              <Tooltip content="Copy email body">
                                <Button
                                  size="micro"
                                  variant="plain"
                                  onClick={() => navigator.clipboard.writeText(r.email_body)}
                                >
                                  Copy email
                                </Button>
                              </Tooltip>
                            </InlineStack>
                            <Text as="p" variant="bodySm" fontWeight="semibold">
                              Cart: {r.product_titles}
                            </Text>
                            <Text as="p" variant="bodySm" fontWeight="semibold">
                              Offer: {r.suggested_offer}
                            </Text>
                            <Text as="p" variant="bodySm" tone="subdued">
                              Subject: {r.email_subject}
                            </Text>
                            <Text as="p" variant="bodyMd">{r.email_body}</Text>
                            <Text as="p" variant="bodySm" tone="subdued">{r.rationale}</Text>
                            {candidate?.recovery_url && (
                              <Link url={candidate.recovery_url} external>
                                Real Shopify checkout recovery link
                              </Link>
                            )}
                          </BlockStack>
                        </Card>
                      );
                    })}
                  </BlockStack>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Abandoned Cart Candidates</Text>
                {recovery.candidates.length === 0 ? (
                  <Text as="p" tone="subdued">No candidates found.</Text>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'numeric', 'numeric']}
                    headings={['Customer', 'Email', 'Cart Contents', 'Cart Value', 'Hours Since Abandoned']}
                    rows={recovery.candidates.map((c) => [
                      c.customer_name,
                      c.email ?? '—',
                      c.product_titles,
                      `$${c.cart_total.toLocaleString()}`,
                      String(c.hours_since_abandoned),
                    ])}
                  />
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

const MIN_HOURS_OPTIONS = [
  { label: '1+ hours since abandoned', value: '1' },
  { label: '6+ hours since abandoned', value: '6' },
  { label: '24+ hours since abandoned', value: '24' },
  { label: '48+ hours since abandoned', value: '48' },
];

export function AbandonedCartPanel() {
  const {
    recoveries, activeRecovery, loading, generating, error,
    total, page, lastPage, fetchRecoveries, generate, deleteRecovery, setActiveRecovery, setError,
  } = useAbandonedCart();

  const [minHoursSinceAbandoned, setMinHoursSinceAbandoned] = useState('1');

  useEffect(() => {
    fetchRecoveries(1);
  }, []);

  const latest = recoveries[0];
  const recoverableRevenue = useMemo(
    () => (latest ? latest.candidates.reduce((sum, c) => sum + c.cart_total, 0) : 0),
    [latest],
  );

  if (activeRecovery) {
    return (
      <RecoveryDetail
        recovery={activeRecovery}
        onBack={() => setActiveRecovery(null)}
        onDelete={deleteRecovery}
      />
    );
  }

  return (
    <Page fullWidth
      title="Abandoned Cart Recovery"
      subtitle="Finds checkouts that were started but never completed, and drafts a personalised recovery email + offer for each"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}

          <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
            <StatCard
              label="Abandoned Carts (latest batch)"
              value={String(latest?.candidates.length ?? 0)}
              icon={CartAbandonedIcon}
              iconColor="orange"
            />
            <StatCard
              label="Recoverable Revenue"
              value={`$${recoverableRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={CashDollarIcon}
              iconColor="green"
              deltaLabel="From cart contents in the latest batch"
            />
            <StatCard
              label="Recovery Batches Run"
              value={String(total)}
              icon={EmailIcon}
              iconColor="blue"
            />
          </InlineGrid>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Generate New Recovery Batch</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Scans checkouts started in the last 14 days and drafts a warm, ready-to-send recovery email
                with a suggested offer for each cart that hasn't converted yet.
              </Text>
              <InlineStack gap="300" blockAlign="end">
                <Box minWidth="240px">
                  <Select label="Minimum wait since abandoned" options={MIN_HOURS_OPTIONS} value={minHoursSinceAbandoned} onChange={setMinHoursSinceAbandoned} />
                </Box>
                <Button
                  variant="primary"
                  loading={generating}
                  onClick={() => generate({ min_hours_since_abandoned: Number(minHoursSinceAbandoned) })}
                >
                  Generate Recovery Batch
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Past Recovery Batches ({total})</Text>

              {loading ? (
                <InlineStack align="center"><Spinner /></InlineStack>
              ) : recoveries.length === 0 ? (
                <EmptyState
                  heading="No abandoned cart recoveries yet"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  action={{ content: 'Generate First Batch', onAction: () => generate({ min_hours_since_abandoned: Number(minHoursSinceAbandoned) }) }}
                >
                  <p>Generate a batch to find abandoned carts worth recovering.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'numeric', 'text']}
                  headings={['Minimum Wait', 'Checkouts Analysed', 'Recommendations', 'Actions']}
                  rows={recoveries.map((r) => [
                    <Button variant="plain" onClick={() => setActiveRecovery(r)}>{`${r.min_hours_since_abandoned}+ hours`}</Button>,
                    String(r.checkouts_analyzed),
                    String(r.ai_recommendations.length),
                    <Button variant="plain" tone="critical" size="slim" onClick={() => deleteRecovery(r.id)}>Delete</Button>,
                  ])}
                />
              )}

              {lastPage > 1 && (
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => fetchRecoveries(page - 1)}
                    hasNext={page < lastPage}
                    onNext={() => fetchRecoveries(page + 1)}
                  />
                </InlineStack>
              )}
            </BlockStack>
          </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
